import { ComponentType } from '@angular/cdk/overlay';
import { ApplicationRef, ComponentRef, EnvironmentInjector, Injectable, Injector, StaticProvider, createComponent, inject } from '@angular/core';

import { WINDOW } from '@frontend/vanilla/core';

import { ModalOptions } from './modal';
import { ModalBackdropComponent } from './modal-backdrop.component';
import { ModalCoverComponent } from './modal-cover.component';
import { ActiveModal, ContentRef } from './modal-ref';

@Injectable({ providedIn: 'root' })
export class ModalWindowStackService {
    private attributes: Extract<keyof ModalOptions, string>[] = [
        'ariaLabelledBy',
        'backdrop',
        'centered',
        'keyboard',
        'size',
        'windowClass',
        'showDialogHeader',
        'title',
        'hideCloseButton',
        'epcotShowBalance',
        'headerTemplate',
        'showCloseBtnText',
        'showBackNav',
        'closePrevious',
        'animatePopupDismiss',
        'showCloseIcon',
        'gestureOptions',
    ];
    private readonly _window = inject(WINDOW);
    private backdropAttributes: (keyof ModalOptions)[] = ['backdropClass'];

    private readonly applicationRef = inject(ApplicationRef);
    private readonly injector = inject(EnvironmentInjector);

    applyBackdropOptions(backdropInstance: ModalBackdropComponent, options: ModalOptions): void {
        this.backdropAttributes.forEach((optionName) => {
            if (options[optionName]) {
                // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                //@ts-ignore dynamic accessor
                // eslint-disable-next-line no-param-reassign
                backdropInstance[optionName] = options[optionName];
            }
        });
    }

    getContentRef(contentInjector: Injector, content: ComponentType<unknown>, context: ActiveModal): ContentRef {
        return this.createFromComponent(contentInjector, content, context);
    }

    createFromComponent(contentInjector: Injector, content: ComponentType<unknown>, context: ActiveModal): ContentRef {
        const providers: StaticProvider[] = [{ provide: ActiveModal, useValue: context }];

        const modalContentInjector = Injector.create({
            providers,
            parent: contentInjector,
        });

        const componentRef = createComponent(content, {
            environmentInjector: this.injector,
            elementInjector: modalContentInjector,
        });
        this.applicationRef.attachView(componentRef.hostView);

        return new ContentRef([[componentRef.location.nativeElement]], componentRef.hostView, componentRef);
    }

    attachBackdrop(containerEl: HTMLElement): ComponentRef<ModalBackdropComponent> {
        const backdropCmptRef = createComponent(ModalBackdropComponent, { environmentInjector: this.injector });
        this.applicationRef.attachView(backdropCmptRef.hostView);
        containerEl.append(backdropCmptRef.location.nativeElement as HTMLElement);

        return backdropCmptRef;
    }

    attachCoverElement(containerEl: HTMLElement): ComponentRef<ModalCoverComponent> {
        const coverCmptRef = createComponent(ModalCoverComponent, { environmentInjector: this.injector });
        this.applicationRef.attachView(coverCmptRef.hostView);
        containerEl.append(coverCmptRef.location.nativeElement as HTMLElement);

        return coverCmptRef;
    }

    applyWindowOptions<T>(windowInstance: T, options: ModalOptions): void {
        this.attributes.forEach((optionName) => {
            if (Object.prototype.hasOwnProperty.call(options, optionName)) {
                // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                //@ts-ignore ignore below accessor
                // eslint-disable-next-line no-param-reassign
                windowInstance[optionName] = options[optionName];
            }
        });
    }

    applyVerticalCentering<T>(windowCmptRef: ComponentRef<T>, containerEl: HTMLElement): void {
        const domEl = windowCmptRef.location.nativeElement as HTMLElement;
        const windowHeight = this._window.innerHeight;

        let recalculateTimes = 0; // the modal content can be async so let's try a few times to position the modal.

        const repositionModal = () => {
            const containerTopOffset = containerEl.getBoundingClientRect().top;
            const newHeight = domEl.clientHeight;
            const newTop = (windowHeight - containerTopOffset) / 2 - newHeight / 2;
            domEl.style.top = `${newTop}px`;
            recalculateTimes++;

            if (recalculateTimes >= 3 || newHeight >= 100) {
                // if the height is greater than 100 most likely the content is loaded
                return;
            }

            setTimeout(repositionModal, recalculateTimes * 10);
        };

        setTimeout(repositionModal, recalculateTimes * 10);
    }

    attachWindowComponent<T>(containerEl: HTMLElement, contentRef: ContentRef, component: ComponentType<T>): ComponentRef<T> {
        const windowCmptRef = createComponent<T>(component, {
            environmentInjector: this.injector,
            projectableNodes: contentRef.nodes,
        });
        this.applicationRef.attachView(windowCmptRef.hostView);
        containerEl.append(windowCmptRef.location.nativeElement as Node);

        return windowCmptRef as ComponentRef<T>;
    }
}
